import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, Calendar, Wrench, Plus } from 'lucide-react';
import { useForm } from '../../hooks/useForm';
import { useToast } from '../Toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import TextArea from '../ui/Textarea';
import SearchInput from '../ui/SearchInput';
import ordensServicoService, { prioridades, calcularIVA, calcularTotalComIVA, formatValor } from '../../services/ordensServico';
import clientesService from '../../services/clientes';
import equipamentosService from '../../services/equipamentos';
import apiService from '../../services/api';
const OrdemServicoForm = ({ ordemServico, clienteId, equipamentoId, onSuccess, onCancel }) => {
    const { success, error } = useToast();
    const queryClient = useQueryClient();
    // Estados locais
    const [selectedClienteId, setSelectedClienteId] = useState(clienteId || ordemServico?.clienteId);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [equipamentosDoCliente, setEquipamentosDoCliente] = useState([]);
    const [isLoadingCliente, setIsLoadingCliente] = useState(false);
    const [isLoadingEquipamentos, setIsLoadingEquipamentos] = useState(false);
    const [valorCalculado, setValorCalculado] = useState({
        valorOrcamento: ordemServico?.valorOrcamento || 0,
        valorIva: ordemServico?.valorIva || 0,
        valorTotal: ordemServico?.valorTotalComIva || 0
    });
    // Queries
    const { data: clientes } = useQuery({
        queryKey: ['clientes', 'all'],
        queryFn: () => clientesService.getClientes({ limit: 1000 }),
        select: (data) => data.data
    });
    const { data: tecnicos } = useQuery({
        queryKey: ['usuarios', 'tecnicos'],
        queryFn: async () => {
            const response = await apiService.get('/usuarios?tipo=TECNICO');
            return response.data || [];
        }
    });
    // Função de busca para autocomplete
    const searchClientes = async (term) => {
        try {
            const response = await clientesService.getClientes({ search: term, limit: 10 });
            return response.data.map(cliente => ({
                id: cliente.id,
                nome: cliente.nome,
                subtitle: cliente.telefonePrincipal || cliente.email || 'Sem contacto',
                tipo: 'Cliente'
            }));
        }
        catch (error) {
            console.error('Erro ao buscar clientes:', error);
            return [];
        }
    };
    // Form
    const { values, errors, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = useForm({
        initialValues: {
            clienteId: clienteId || ordemServico?.clienteId || '',
            equipamentoId: equipamentoId || ordemServico?.equipamentoId || '',
            usuarioResponsavelId: ordemServico?.usuarioResponsavelId || '',
            problemaRelatado: ordemServico?.problemaRelatado || '',
            diagnostico: ordemServico?.diagnostico || '',
            solucao: ordemServico?.solucao || '',
            prioridade: ordemServico?.prioridade || 'NORMAL',
            valorOrcamento: ordemServico?.valorOrcamento || 0,
            dataPrevistaConclusao: ordemServico?.dataPrevistaConclusao
                ? new Date(ordemServico.dataPrevistaConclusao).toISOString().split('T')[0]
                : '',
            passwordEquipamento: ordemServico?.passwordEquipamento || '',
            acessorios: ordemServico?.acessorios || '',
            observacoesInternas: ordemServico?.observacoesInternas || ''
        },
        validationRules: {
            clienteId: {
                required: 'Cliente é obrigatório'
            },
            equipamentoId: {
                required: 'Equipamento é obrigatório'
            },
            problemaRelatado: {
                required: 'Problema relatado é obrigatório',
                minLength: { value: 10, message: 'Problema deve ter pelo menos 10 caracteres' }
            },
            prioridade: {
                required: 'Prioridade é obrigatória'
            }
        },
        onSubmit: async (formValues) => {
            try {
                const data = {
                    ...formValues,
                    clienteId: parseInt(formValues.clienteId.toString()),
                    equipamentoId: parseInt(formValues.equipamentoId.toString()),
                    usuarioResponsavelId: formValues.usuarioResponsavelId ? parseInt(formValues.usuarioResponsavelId.toString()) : undefined,
                    valorOrcamento: valorCalculado.valorOrcamento
                };
                let result;
                if (ordemServico) {
                    result = await ordensServicoService.update(ordemServico.id, data);
                    success('Ordem de serviço atualizada com sucesso');
                }
                else {
                    result = await ordensServicoService.create(data);
                    success('Ordem de serviço criada com sucesso');
                }
                // Invalidar queries relacionadas
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['ordens-servico'] }),
                    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
                    queryClient.invalidateQueries({ queryKey: ['dashboard', 'metrics'] }),
                    queryClient.refetchQueries({ queryKey: ['ordens-servico'] }),
                    queryClient.resetQueries({ queryKey: ['ordens-servico'] })
                ]);
                onSuccess(result);
            }
            catch (err) {
                error('Erro ao salvar ordem de serviço', err.response?.data?.error || err.message);
            }
        }
    });
    // Efeitos
    // Carregar cliente quando clienteId for passado como prop
    useEffect(() => {
        if (clienteId && !selectedCliente) {
            setIsLoadingCliente(true);
            clientesService.getClienteById(clienteId)
                .then((cliente) => {
                setSelectedCliente(cliente);
                setSelectedClienteId(cliente.id);
                setFieldValue('clienteId', cliente.id.toString());
            })
                .catch((err) => {
                console.error('Erro ao carregar cliente:', err);
            })
                .finally(() => setIsLoadingCliente(false));
        }
    }, [clienteId]);
    // Carregar equipamentos do cliente
    useEffect(() => {
        if (selectedClienteId) {
            setIsLoadingEquipamentos(true);
            equipamentosService.getByCliente(selectedClienteId)
                .then((equipamentos) => {
                setEquipamentosDoCliente(equipamentos);
                // Se equipamentoId foi passado como prop, definir automaticamente
                if (equipamentoId && !values.equipamentoId) {
                    setFieldValue('equipamentoId', equipamentoId.toString());
                }
            })
                .catch(() => setEquipamentosDoCliente([]))
                .finally(() => setIsLoadingEquipamentos(false));
        }
        else {
            setEquipamentosDoCliente([]);
        }
    }, [selectedClienteId, equipamentoId]);
    // Carregar cliente quando selecionado na lista
    useEffect(() => {
        if (selectedClienteId && clientes && !clienteId) {
            const cliente = clientes.find(c => c.id === selectedClienteId);
            setSelectedCliente(cliente || null);
        }
    }, [selectedClienteId, clientes, clienteId]);
    useEffect(() => {
        const valor = parseFloat(values.valorOrcamento.toString()) || 0;
        const iva = calcularIVA(valor);
        const total = calcularTotalComIVA(valor);
        setValorCalculado({
            valorOrcamento: valor,
            valorIva: iva,
            valorTotal: total
        });
    }, [values.valorOrcamento]);
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-6", children: [_jsxs("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6", children: [_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Cliente *" }), clienteId ? (
                                    // Se clienteId foi passado como prop, mostrar como campo readonly
                                    _jsx(Input, { value: selectedCliente?.nome || (isLoadingCliente ? 'Carregando...' : ''), disabled: true, readOnly: true })) : (
                                    // Senão, mostrar campo de busca
                                    _jsx(SearchInput, { placeholder: "Digite o nome do cliente...", onSearch: searchClientes, onSelect: (result) => {
                                            const cliente = clientes?.find(c => c.id === result.id);
                                            setSelectedCliente(cliente || null);
                                            setSelectedClienteId(result.id);
                                            setFieldValue('clienteId', result.id.toString());
                                            setFieldValue('equipamentoId', ''); // Reset equipamento quando cliente mudar
                                        }, value: selectedCliente?.nome || '', emptyMessage: "Nenhum cliente encontrado", minSearchLength: 2, error: errors.clienteId }))] }), _jsx(Select, { label: "Equipamento *", disabled: !selectedClienteId || isLoadingEquipamentos, ...getFieldProps('equipamentoId'), options: [
                                    { value: '', label: isLoadingEquipamentos ? 'Carregando equipamentos...' : 'Selecione um equipamento' },
                                    ...equipamentosDoCliente.map(equipamento => ({
                                        value: equipamento.id.toString(),
                                        label: `${equipamento.modelo.marca.nome} ${equipamento.modelo.nome} ${equipamento.numeroSerie ? `(S/N: ${equipamento.numeroSerie})` : ''}`
                                    }))
                                ] }), _jsx(Select, { label: "T\u00E9cnico Respons\u00E1vel", ...getFieldProps('usuarioResponsavelId'), options: [
                                    { value: '', label: 'Não atribuído' },
                                    ...(tecnicos?.map(tecnico => ({
                                        value: tecnico.id.toString(),
                                        label: tecnico.nome
                                    })) || [])
                                ] }), _jsx(Select, { label: "Prioridade *", ...getFieldProps('prioridade'), options: prioridades.map(prioridade => ({
                                    value: prioridade.value,
                                    label: prioridade.label
                                })) }), _jsx(Input, { label: "Data Prevista de Conclus\u00E3o", type: "date", ...getFieldProps('dataPrevistaConclusao'), leftIcon: _jsx(Calendar, { className: "h-4 w-4" }) })] }), _jsxs("div", { className: "space-y-6", children: [_jsx(TextArea, { label: "Problema Relatado *", placeholder: "Descreva o problema relatado pelo cliente...", rows: 3, ...getFieldProps('problemaRelatado') }), _jsx(TextArea, { label: "Diagn\u00F3stico", placeholder: "Diagn\u00F3stico t\u00E9cnico do problema...", rows: 3, ...getFieldProps('diagnostico') }), _jsx(TextArea, { label: "Solu\u00E7\u00E3o", placeholder: "Descri\u00E7\u00E3o da solu\u00E7\u00E3o aplicada...", rows: 3, ...getFieldProps('solucao') }), _jsx(Input, { label: "Password do Equipamento", placeholder: "Password/PIN do equipamento", ...getFieldProps('passwordEquipamento') }), _jsx(TextArea, { label: "Acess\u00F3rios Entregues", placeholder: "Liste os acess\u00F3rios entregues com o equipamento...", rows: 2, ...getFieldProps('acessorios') })] })] }), _jsxs("div", { className: "bg-gray-50 p-4 rounded-lg", children: [_jsxs("h3", { className: "text-lg font-medium text-gray-900 mb-4 flex items-center gap-2", children: [_jsx(Calculator, { className: "h-5 w-5" }), "Valores do Or\u00E7amento"] }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: [_jsxs("div", { children: [_jsx(Input, { label: "Estimativa Inicial (\u20AC)", type: "number", step: "0.01", min: "0", placeholder: "Valor estimado para informar ao cliente", ...getFieldProps('valorOrcamento') }), _jsx("p", { className: "mt-1 text-xs text-gray-500", children: "Este \u00E9 apenas um valor estimado. O valor real ser\u00E1 definido ap\u00F3s o diagn\u00F3stico." })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "IVA (23%)" }), _jsx("div", { className: "px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-900", children: formatValor(valorCalculado.valorIva) })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Total com IVA" }), _jsx("div", { className: "px-3 py-2 bg-blue-50 border border-blue-300 rounded-md text-sm font-medium text-blue-900", children: formatValor(valorCalculado.valorTotal) })] })] })] }), _jsx(TextArea, { label: "Observa\u00E7\u00F5es Internas", placeholder: "Observa\u00E7\u00F5es internas para a equipa t\u00E9cnica...", rows: 3, ...getFieldProps('observacoesInternas') }), _jsxs("div", { className: "flex justify-end gap-3 pt-6 border-t border-gray-200", children: [_jsx(Button, { type: "button", variant: "outline", onClick: onCancel, children: "Cancelar" }), _jsx(Button, { type: "submit", isLoading: isSubmitting, leftIcon: ordemServico ? _jsx(Wrench, { className: "h-4 w-4" }) : _jsx(Plus, { className: "h-4 w-4" }), children: ordemServico ? 'Atualizar OS' : 'Criar OS' })] })] }));
};
export default OrdemServicoForm;
